import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Filter } from "lucide-react";
import { ProjectCard } from "../components/ProjectCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: "all", label: "All Projects" },
  { value: "commercial", label: "Commercial" },
  { value: "residential", label: "Residential" },
  { value: "interior", label: "Interior Build-Outs" },
];

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        setFilteredProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter((p) => p.category === activeCategory));
    }
  }, [activeCategory, projects]);

  return (
    <div data-testid="projects-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A] overflow-hidden" data-testid="projects-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920"
            alt="Projects"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            Our Portfolio
          </span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-6">
            Featured Projects
          </h1>
          <div className="section-divider" />
          <p className="text-[#A1A1AA] text-xl max-w-2xl mt-6">
            Explore our portfolio of completed commercial, residential, and interior
            construction projects across New Jersey and New York.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-[#121212] border-b border-[#2A2A2A] sticky top-[73px] z-40" data-testid="filter-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <Filter size={18} />
              <span className="text-sm uppercase tracking-wider">Filter:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setActiveCategory(cat.value)}
                  data-testid={`filter-${cat.value}`}
                  className={`px-6 py-2 text-sm uppercase tracking-wider transition-colors ${
                    activeCategory === cat.value
                      ? "bg-[#7ED4C6] text-[#0A0A0A]"
                      : "bg-[#1E1E1E] text-white hover:bg-[#2A2A2A]"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-[#121212] animate-pulse" />
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#A1A1AA] text-lg">No projects found in this category.</p>
            </div>
          ) : (
            <div className="masonry">
              {filteredProjects.map((project, index) => (
                <div key={project.id} className="masonry-item">
                  <ProjectCard project={project} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#121212] border-y border-[#2A2A2A]" data-testid="project-stats">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="stat-number">250+</div>
              <p className="text-[#A1A1AA] uppercase tracking-wider text-sm mt-2">
                Projects Completed
              </p>
            </div>
            <div>
              <div className="stat-number">50+</div>
              <p className="text-[#A1A1AA] uppercase tracking-wider text-sm mt-2">
                Commercial Projects
              </p>
            </div>
            <div>
              <div className="stat-number">150+</div>
              <p className="text-[#A1A1AA] uppercase tracking-wider text-sm mt-2">
                Residential Projects
              </p>
            </div>
            <div>
              <div className="stat-number">50+</div>
              <p className="text-[#A1A1AA] uppercase tracking-wider text-sm mt-2">
                Interior Build-Outs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#7ED4C6]" data-testid="projects-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6">
            Have a Project in Mind?
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8">
            Let's discuss how we can bring your vision to life with the same quality
            and precision you see in our portfolio.
          </p>
          <Link
            to="/contact"
            className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
            data-testid="projects-contact-btn"
          >
            Start Your Project
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
